import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import {
  FileText,
  Plus,
  Minus,
  Info,
  Code2
} from 'lucide-react';
import * as Diff from 'diff';
import { cn } from '../../lib/utils';

interface YAMLDiffPreviewProps {
  yamlContent: string;
  updateMode: 'append' | 'replace' | 'new_file' | 'smart_merge';
  existingContent?: string;
  className?: string;
}

// Create GitHub-style diff using Myers algorithm (from ChatChangelog.tsx)
const createDiff = (oldText: string, newText: string): Array<{line: string, type: 'unchanged' | 'removed' | 'added'}> => {
  const changes = Diff.diffLines(oldText, newText);
  const result: Array<{line: string, type: 'unchanged' | 'removed' | 'added'}> = [];

  changes.forEach(change => {
    const lines = change.value.split('\n');
    if (lines[lines.length - 1] === '') {
      lines.pop();
    }

    lines.forEach(line => {
      if (change.removed) {
        result.push({ line, type: 'removed' });
      } else if (change.added) {
        result.push({ line, type: 'added' });
      } else {
        result.push({ line, type: 'unchanged' });
      }
    });
  });

  return result;
};

export const YAMLDiffPreview: React.FC<YAMLDiffPreviewProps> = ({
  yamlContent,
  updateMode,
  existingContent = '',
  className = '',
}) => {
  // Calculate diff statistics
  let diffStats = { linesAdded: 0, linesRemoved: 0, totalLines: 0 };
  let diffLines: Array<{line: string, type: 'unchanged' | 'removed' | 'added'}> = [];

  if (existingContent && (updateMode === 'smart_merge' || updateMode === 'replace')) {
    diffLines = createDiff(existingContent, yamlContent);
    diffStats = diffLines.reduce((stats, line) => {
      if (line.type === 'added') stats.linesAdded++;
      if (line.type === 'removed') stats.linesRemoved++;
      stats.totalLines++;
      return stats;
    }, { linesAdded: 0, linesRemoved: 0, totalLines: 0 });
  } else {
    // For new files or append mode, all lines are additions
    const lines = yamlContent.split('\n');
    diffStats.linesAdded = lines.length;
    diffStats.totalLines = lines.length;
    diffLines = lines.map(line => ({ line, type: 'added' as const }));
  }

  // Get mode description
  const getModeDescription = () => {
    switch (updateMode) {
      case 'append':
        return 'Your YAML will be appended to the existing file with a document separator (---)';
      case 'replace':
        return 'The entire file contents will be replaced with your YAML';
      case 'new_file':
        return 'A new file will be created with your YAML content';
      case 'smart_merge':
        return 'Intelligent merge will combine your YAML with existing content, preserving other resources';
      default:
        return '';
    }
  };

  // Get mode color (theme-aware)
  const getModeColor = () => {
    switch (updateMode) {
      case 'append':
        return 'bg-blue-600 dark:bg-blue-500';
      case 'replace':
        return 'bg-orange-600 dark:bg-orange-500';
      case 'new_file':
        return 'bg-emerald-600 dark:bg-emerald-500';
      case 'smart_merge':
        return 'bg-purple-600 dark:bg-purple-500';
      default:
        return 'bg-slate-600 dark:bg-slate-500';
    }
  };

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="h-4 w-4" />
          YAML Content Preview
        </CardTitle>

        {/* Update Mode Info */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <Badge className={`${getModeColor()} text-white dark:text-white flex-shrink-0`}>
            {updateMode.replace('_', ' ').toUpperCase()}
          </Badge>
          <span className="text-sm text-muted-foreground break-words">
            {getModeDescription()}
          </span>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm flex-wrap">
          <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
            <Plus className="h-3 w-3" />
            <span>{diffStats.linesAdded} lines added</span>
          </div>
          {diffStats.linesRemoved > 0 && (
            <div className="flex items-center gap-1 text-red-600 dark:text-red-400 whitespace-nowrap">
              <Minus className="h-3 w-3" />
              <span>{diffStats.linesRemoved} lines removed</span>
            </div>
          )}
          <div className="text-muted-foreground whitespace-nowrap">
            Total: {diffStats.totalLines} lines
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {/* Smart Merge Info */}
        {updateMode === 'smart_merge' && (
          <div className="p-4 border-b">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription className="break-words">
                Smart merge will intelligently combine your YAML with existing content.
                Resources with the same kind/name/namespace will be merged at the field level,
                while new resources will be added.
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* YAML Content Display */}
        <div className="relative">
          <div className="bg-muted/30 border-b px-3 py-2 text-sm font-medium flex items-center gap-2">
            <Code2 className="h-4 w-4" />
            {existingContent && (updateMode === 'smart_merge' || updateMode === 'replace')
              ? 'YAML Diff Preview'
              : 'YAML Content to Deploy'}
          </div>

          {/* Professional diff display using the same logic as ChatChangelog */}
          <div className="bg-muted p-2 sm:p-3 rounded-none border-none font-mono text-xs sm:text-sm overflow-x-auto max-h-96">
            {diffLines.map((diffLine, lineIndex) => (
              <div
                key={lineIndex}
                className={cn(
                  "whitespace-pre-wrap flex min-w-0",
                  diffLine.type === 'removed' && "bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200",
                  diffLine.type === 'added' && "bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200",
                  diffLine.type === 'unchanged' && "text-foreground hover:bg-muted/50"
                )}
              >
                <span className={cn(
                  "inline-block w-6 sm:w-8 text-right mr-2 sm:mr-3 text-muted-foreground select-none flex-shrink-0 border-r border-border/50 pr-1 sm:pr-2",
                  diffLine.type === 'removed' && "text-red-600 dark:text-red-400 bg-red-100/50 dark:bg-red-900/30",
                  diffLine.type === 'added' && "text-green-600 dark:text-green-400 bg-green-100/50 dark:bg-green-900/30"
                )}>
                  {diffLine.type === 'removed' ? '-' : diffLine.type === 'added' ? '+' : ' '}
                </span>
                <span className="flex-1 min-w-0 break-words">
                  {diffLine.line}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Mode-specific additional info */}
        {updateMode === 'replace' && existingContent && (
          <div className="p-4 border-t bg-orange-50/50 dark:bg-orange-900/20">
            <div className="flex items-center gap-2 text-orange-700 dark:text-orange-300 text-sm font-medium mb-2">
              <Minus className="h-4 w-4" />
              Existing content will be removed
            </div>
            <div className="text-sm text-muted-foreground break-words">
              The current file has {existingContent.split('\n').length} lines that will be replaced with your new content.
            </div>
          </div>
        )}

        {updateMode === 'append' && (
          <div className="p-4 border-t bg-blue-50/50 dark:bg-blue-900/20">
            <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300 text-sm font-medium mb-2">
              <Plus className="h-4 w-4" />
              Content will be appended
            </div>
            <div className="text-sm text-muted-foreground break-words">
              Your YAML will be added to the end of the existing file with a document separator (---).
            </div>
          </div>
        )}

        {updateMode === 'new_file' && (
          <div className="p-4 border-t bg-emerald-50/50 dark:bg-emerald-900/20">
            <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300 text-sm font-medium mb-2">
              <FileText className="h-4 w-4" />
              New file will be created
            </div>
            <div className="text-sm text-muted-foreground break-words">
              A new file will be created with your YAML content.
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};